import { Icon } from './Icon'
import { Text } from './Text'
import { cn } from '@lib/cn'
import NextLink from 'next/link'
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  HTMLAttributes,
  forwardRef,
} from 'react'

export type IconButtonVariant = 'light' | 'dark'
interface IconButtonProps {
  variant: IconButtonVariant
  icon:
    | 'caret-down'
    | 'chevron-left'
    | 'chevron-right'
    | 'rolex-chevron-right'
    | 'rolex-chevron-left'
    | 'rolex-refresh-arrow'
    | 'rolex-caret-down'
    | 'rolex-mobile-menu-arrow'
    | 'rolex-back-to-top-arrow'
    | 'mail'
    | 'phone'
    | 'location'
    | 'close-map-location'
    | 'directions'
    | 'externalLink'
    | 'plus'
    | 'minus'
    | 'cross'
    | 'whatsapp'
    | 'chat'
    | 'reset'
    | 'info'
  children?: never
  label?: string
}

export type IconButtonType = ButtonHTMLAttributes<HTMLButtonElement> &
  IconButtonProps & {
    as: 'button'
    status?: 'idle' | 'disabled'
    disabled?: boolean
  }

export type IconButtonLinkType = AnchorHTMLAttributes<HTMLAnchorElement> &
  IconButtonProps & {
    href: string
    status?: never
    as: 'link'
  }

export type IconButtonSpanType = HTMLAttributes<HTMLSpanElement> &
  IconButtonProps & {
    href?: never
    status?: never
    as: 'span'
  }

export const IconButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonType | IconButtonLinkType | IconButtonSpanType
>(
  (
    {
      as: Component = 'button',
      variant = 'dark',
      icon,
      label,
      children,
      className = '',
      status,
      ...rest
    },
    ref,
  ) => {
    const finalClassName = cn(
      'group w-auto flex gap-2 items-center',
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )

    if (Component === 'link') {
      const props = rest as Omit<IconButtonLinkType, 'as'>
      const { href } = props
      return (
        <NextLink
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          href={href}
          className={finalClassName}
          {...props}
        >
          <InnerButton />
        </NextLink>
      )
    }

    if (Component === 'span') {
      return (
        <span
          ref={ref as ForwardedRef<HTMLSpanElement>}
          className={finalClassName}
          {...(rest as Omit<IconButtonType, 'as'>)}
        >
          <InnerButton />
        </span>
      )
    }

    return (
      <Component
        ref={ref as ForwardedRef<HTMLButtonElement>}
        className={finalClassName}
        disabled={status === 'disabled'}
        {...(rest as Omit<IconButtonType, 'as'>)}
      >
        <InnerButton />
      </Component>
    )

    function InnerButton() {
      return (
        <>
          <div
            className={cn(
              'flex items-center justify-center rounded-full transition-all duration-100',
              label ? 'h-[36px] w-[36px]' : 'h-[44px] w-[44px]',
              label && variant === 'light' && 'group-hover:text-rolexPrimary',
              variant === 'light' &&
                'bg-rolexGrey/30 text-rolexBrown group-hover:bg-rolexGrey/[0.15]',
              variant === 'dark' &&
                'bg-rolexDarkGrey/40 text-white group-hover:bg-rolexDarkGrey/20',
            )}
          >
            <Icon icon={icon as any} className="h-5 w-5" />
          </div>
          {label && (
            <Text
              className={cn(
                'hidden md:block',
                variant === 'light' && 'text-rolexBrown',
                variant === 'dark' && 'text-white',
                label && variant === 'light' ? 'group-hover:text-rolexPrimary' : '',
              )}
              variant="fixed14"
            >
              {label}
            </Text>
          )}
        </>
      )
    }
  },
)

IconButton.displayName = 'IconButton'
